import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ClientBankAccountNumber } from '@/components/common/ClientBankAccountNumber'
import { DeskTable, muted, type Column } from '@/components/common/DeskTable'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import FilterSearch from '@/components/common/FilterSearch'
import PageFrame from '@/components/common/PageFrame'
import { StatusChips } from '@/components/common/StatusChips'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { GlassSelect } from '@/components/ui/glass-select'
import { useListPageSize } from '@/hooks/useListPageSize'
import { backofficeApi, type ClientBankRow } from '@/lib/api-client'
import { dash } from '@/lib/format'
import { FILTER_BAR } from '@/lib/ui-shell'

const ACCOUNT_TYPE_LABELS: Record<string, string> = { GIRO: 'Giro', TABUNGAN: 'Tabungan', LAINNYA: 'Lainnya' }
const SEGMENT_LABELS: Record<string, string> = { real: 'Real', demo: 'Demo', test: 'Test' }
const accountTypeLabel = (type: string) => ACCOUNT_TYPE_LABELS[type] ?? type

const columns: Column<ClientBankRow>[] = [
  {
    key: 'client',
    header: 'Client',
    sticky: true,
    mobile: 'title',
    className: 'overflow-hidden',
    cell: (r) => (
      <>
        <div className="truncate font-semibold" title={r.full_name}>{r.full_name}</div>
        <div className="truncate font-mono text-[12.5px] text-muted-foreground" title={r.client_code || undefined}>{dash(r.client_code)}</div>
      </>
    ),
  },
  { key: 'email', header: 'Email', mobile: 'subtitle', className: 'overflow-hidden', cell: (r) => <span className="block truncate" title={r.email}>{r.email}</span> },
  { key: 'accounts', header: 'Accounts', align: 'right', mobile: 'field', cell: (r) => r.accounts.length },
  { key: 'bank', header: 'Primary bank', mobile: 'field', cell: (r) => muted(dash(r.accounts.find((a) => a.is_primary)?.bank_name)) },
  { key: 'segment', header: 'Segment', mobile: 'field', cell: (r) => SEGMENT_LABELS[r.segment] ?? r.segment },
]

export function ClientBanksPage() {
  const [sp, setSp] = useSearchParams()
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const segment = sp.get('segment') || 'real'
  const accountType = sp.get('account_type') || ''
  const appliedQ = sp.get('q') || ''
  const [searchDraft, setSearchDraft] = useState(appliedQ)
  const [open, setOpen] = useState<ClientBankRow | null>(null)
  const rowRef = useRef<HTMLElement | null>(null)

  function patch(next: Record<string, string>) {
    const cur = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(next)) {
      if (v) cur.set(k, v)
      else cur.delete(k)
    }
    if (!('page' in next)) cur.set('page', '1')
    setSp(cur, { replace: true })
  }

  const q = useQuery({
    queryKey: ['client-banks', appliedQ, accountType, segment, page, pageSize],
    queryFn: () =>
      backofficeApi.clientBanks({
        q: appliedQ || undefined,
        account_type: accountType || undefined,
        segment,
        page,
        page_size: pageSize,
      }),
  })
  const items = q.data?.items ?? []

  return (
    <PageFrame title="Client Banks" description="Client withdrawal-payout accounts, grouped by client. Select a client to see each account.">
      <FilterBarForm onSubmit={() => patch({ q: searchDraft.trim() })} className={FILTER_BAR}>
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <FilterSearch value={searchDraft} onChange={setSearchDraft} placeholder="Name, email, client code" />
          <Field label="Account type" className="min-w-36 max-lg:w-full">
            <GlassSelect
              value={accountType}
              onChange={(next) => patch({ account_type: next })}
              options={[
                { value: '', label: 'All types' },
                { value: 'GIRO', label: 'Giro' },
                { value: 'TABUNGAN', label: 'Tabungan' },
                { value: 'LAINNYA', label: 'Lainnya' },
              ]}
            />
          </Field>
          <div className="flex flex-col gap-1.5 max-lg:w-full">
            <span id="segment-label" className="text-[13px] font-semibold text-muted-foreground">Segment</span>
            <StatusChips
              labelledBy="segment-label"
              value={segment}
              onChange={(next) => patch({ segment: next === 'real' ? '' : next })}
              options={[
                { id: 'real', label: 'Real' },
                { id: 'demo', label: 'Demo' },
                { id: 'test', label: 'Test' },
                { id: 'all', label: 'All' },
              ]}
            />
          </div>
          <FilterBarApply
            isDirty={searchDraft !== appliedQ}
            canReset={Boolean(searchDraft || appliedQ || accountType)}
            onReset={() => {
              setSearchDraft('')
              patch({ q: '', account_type: '' })
            }}
          />
        </div>
      </FilterBarForm>

      <DeskTable
        columns={columns}
        rows={items}
        rowKey={(r) => r.client_id}
        selectedKey={open?.client_id}
        onRowClick={(row, el) => {
          rowRef.current = el
          setOpen(row)
        }}
        loading={q.isLoading}
        error={q.error}
        emptyTitle="No client banks"
        emptyDescription="Nothing matches the search and filters above. Reset them to see every client."
        paging={{
          total: q.data?.total ?? 0,
          page,
          pageSize,
          onPage: (p) => patch({ page: String(p) }),
          onPageSize: (size) => patch({ page_size: String(size), page: '1' }),
        }}
      />

      <Sheet open={Boolean(open)} onClose={() => setOpen(null)} label="Client bank accounts" returnFocusRef={rowRef}>
        {open ? (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold break-words">{open.full_name}</h2>
                <p className="font-mono text-[12.5px] text-muted-foreground">{dash(open.client_code)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpen(null)}>
                Close
              </Button>
            </div>
            <ul className="divide-y divide-border">
              {open.accounts.map((acct) => (
                <li key={acct.id} className="space-y-1.5 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{acct.bank_name}</span>
                    {acct.is_primary ? <Badge variant="outline">Primary</Badge> : null}
                  </div>
                  <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-sm">
                    <dt className="text-muted-foreground">Number</dt>
                    <dd>
                      <ClientBankAccountNumber account={acct} />
                    </dd>
                    <dt className="text-muted-foreground">Holder</dt>
                    <dd className="break-words">{dash(acct.account_holder)}</dd>
                    <dt className="text-muted-foreground">Branch</dt>
                    <dd className="break-words">{dash(acct.branch)}</dd>
                    <dt className="text-muted-foreground">Type</dt>
                    <dd>{accountTypeLabel(acct.account_type)}</dd>
                  </dl>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Sheet>
    </PageFrame>
  )
}
